import cn from 'classnames';
import { ICellProps } from '@creditflowresearch/uilib/Table/DataTable';
import Status from '@/creditflowresearch/ui/common/components/TableCells/DealStatus';
import UpsizedCell from '@/creditflowresearch/ui/common/components/TableCells/UpsizedCell';
import TickerCell from '@/creditflowresearch/ui/common/components/TableCells/TickerCell';
import TrancheSizeCell from '@/creditflowresearch/ui/common/components/TableCells/TrancheSizeCell';
import CorporateLeads from '@/creditflowresearch/ui/components/deals/DealListTable/cells/CorporateLeads';
import {
  formatDataCell,
  formatNumberWithDecimals,
  formatTenorCell,
  getTrancheFieldWithPercent,
} from '@/creditflowresearch/ui/components/deals/helpers';
import IndustryCell from '@/creditflowresearch/ui/common/components/TableCells/IndustryCell';
import IssuersCell from '@/creditflowresearch/ui/components/deals/DealListTable/cells/IssuersCell';
import DealLink from '@/creditflowresearch/ui/components/deals/DealListTable/cells/DealLink';
import DealDateCell from '@/creditflowresearch/ui/common/components/TableCells/DealDateCell';
import { createRowLinkColumn } from '@/creditflowresearch/ui/common/components/DataTable/rowLink';
import type IDealCellData from '@/creditflowresearch/ui/components/deals/DealListTable/cells/types';
import { Product } from '@/creditflowresearch/domain/Product';
import MaturityDateCell from '@/creditflowresearch/ui/common/components/TableCells/MaturityDateCell';
import { RegionCell } from '@/creditflowresearch/ui/common/components/DataCells';
import { RATING_AGENCIES_MAP } from '@/creditflowresearch/ui/components/bonds/helpers';

import TruncateText from '@creditflowresearch/uilib/TruncateText';
import alignStyles from '@creditflowresearch/uilib/styles/helpers/align.module.scss';
import spaceStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import textStyles from '@creditflowresearch/uilib/styles/helpers/text.module.scss';

export const getCorporateMarketColumns = () => ([
  {
    name: 'industry',
    title: 'Industry',
    width: 73,
    minWidth: 73,
    className: alignStyles.alignCenter,
    cellCallback: ({ row: { industry, subindustry } }) => (
      <IndustryCell industry={ industry } subindustry={ subindustry } />
    ),
  },
  {
    name: 'status',
    title: 'Status',
    width: 66,
    minWidth: 66,
    className: alignStyles.alignCenter,
    cellCallback: ({ row }) => <Status status={ row.status } />,
  },
  {
    name: 'date',
    title: 'Date',
    width: 106,
    minWidth: 106,
    className: alignStyles.alignLeft,
    cellCallback: ({ row }) => <DealDateCell row={ row } />,
  },
  {
    name: 'ticker',
    title: 'Ticker(s)',
    className: alignStyles.alignCenter,
    width: 70,
    minWidth: 70,
    cellCallback: (cellProps: ICellProps) => <TickerCell cell={ cellProps } isDeal isBadge />,
  },
  {
    name: 'primaryIssuer',
    title: 'issuer(s)',
    maxWidth: 293,
    cellCallback: (cellProps) => <IssuersCell { ...cellProps } maxWidth={ 293 } />,
  },
  {
    name: 'moRatings',
    title: 'MO',
    className: alignStyles.alignCenter,
    tooltipContent: 'Moody’s Rating',
    width: 53,
    cellCallback: ({ row }) => formatDataCell(row.ratings.find((rating) => rating?.ratingAgencyId === RATING_AGENCIES_MAP.ratingMoodys)?.abbreviation, '-'),
  },
  {
    name: 'spRatings',
    title: 'SP',
    className: alignStyles.alignCenter,
    tooltipContent: 'S&P Rating',
    width: 53,
    cellCallback: ({ row }) => formatDataCell(row.ratings.find((rating) => rating?.ratingAgencyId === RATING_AGENCIES_MAP.ratingSAndP)?.abbreviation, '-'),
  },
  {
    name: 'fitchRatings',
    title: 'FI',
    className: alignStyles.alignCenter,
    tooltipContent: 'Fitch Rating',
    width: 53,
    cellCallback: ({ row }) => formatDataCell(row.ratings.find((rating) => rating?.ratingAgencyId === RATING_AGENCIES_MAP.ratingFitch)?.abbreviation, '-'),
  },
  {
    name: 'size',
    title: 'Size (M)',
    width: 79,
    className: alignStyles.alignRight,
    cellCallback: ({ row: { size, benchmarkSize, status, sizeDescription } }) => (
      <TrancheSizeCell
        size={ size.size }
        currency={ size }
        sizeDescription={ sizeDescription }
        status={ status }
        benchmarkSize={ benchmarkSize }
      />
    ),
  },
  {
    name: 'upsized',
    width: 12,
    maxWidth: 12,
    className: cn(alignStyles.alignLeft, spaceStyles.pn),
    cellCallback: ({ row: { upsized } }) => <UpsizedCell upsized={ !!upsized } />,
  },
  {
    name: 'ranking',
    title: 'Ranking',
    className: alignStyles.alignRight,
    width: 95,
    maxWidth: 120,
    cellCallback: ({ row: { ranking } }: ICellProps) => (
      formatDataCell(ranking?.name && <TruncateText value={ ranking.name } />)
    ),
  },
  {
    name: 'tenor',
    title: 'TNR',
    width: 61,
    className: alignStyles.alignRight,
    cellCallback: ({ row: { tenor, perpetual } }: ICellProps) => formatTenorCell(tenor, perpetual, 2),
  },
  {
    name: 'call',
    title: 'Call',
    width: 49,
    className: alignStyles.alignRight,
    cellCallback: ({ row: { call } }: ICellProps) => (call ? formatNumberWithDecimals(call, 2) : '-'),
  },
  {
    name: 'maturity',
    title: 'MTY',
    minWidth: 73,
    className: alignStyles.alignRight,
    cellCallback: ({ row }) => <MaturityDateCell row={ row } />,
  },
  {
    name: 'coupon',
    title: 'Coupon',
    width: 82,
    className: cn(alignStyles.alignRight, textStyles.nowrap),
    cellCallback: ({ row: { coupon } }: ICellProps) => formatDataCell(getTrancheFieldWithPercent(coupon, 3)),
  },
  {
    name: 'leads',
    title: 'Leads',
    width: 90,
    className: alignStyles.alignRight,
    cellCallback: (cellProps: ICellProps) => (
      <CorporateLeads { ...cellProps } limit={ 2 } />
    ),
  },
  {
    name: 'product',
    title: 'product',
    className: alignStyles.alignCenter,
    width: 77,
    cellCallback: ({ row }) => Product.getAbbreviation(row.productId),
  },
  {
    name: 'region',
    title: 'Region',
    className: alignStyles.alignLeft,
    width: 77,
    cellCallback: ({ row }) => <RegionCell regionId={ row.regionId } countryId={ row.countryId } />,
  },
  {
    name: 'link',
    className: alignStyles.alignCenter,
    width: 32,
    cellCallback: DealLink,
  },
  createRowLinkColumn<IDealCellData>((row) => row.link),
]);
